import React, { useState, useEffect } from 'react';
import { taskAPI, facilityAPI } from '../services/api';
import api from '../services/api';
import { BarChart2, Download, FileText, TrendingUp, CheckCircle, AlertTriangle, Loader } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const REPORT_TYPES = [
  { id: 'compliance_status', title: 'Compliance Status Report', desc: 'Overall compliance across all facilities and permit conditions', icon: CheckCircle, color: '#0066FF', bg: '#EFF6FF' },
  { id: 'emissions_trend', title: 'Emissions Trend Report', desc: '12-month emissions by pollutant vs permit limits', icon: TrendingUp, color: '#16A34A', bg: '#F0FDF4' },
  { id: 'task_completion', title: 'Task Completion Report', desc: 'On-time completion rate, overdue history, upcoming deadlines', icon: CheckCircle, color: '#7C3AED', bg: '#F5F3FF' },
  { id: 'facility_summary', title: 'Facility Summary Report', desc: 'Full summary of all facilities, sources, and compliance status', icon: FileText, color: '#D97706', bg: '#FFFBEB' },
];

function Reports() {
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState('');
  const [generating, setGenerating] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeReport, setActiveReport] = useState(null);

  useEffect(() => {
    facilityAPI.getAll().then(res => {
      const facs = res.data.facilities || [];
      setFacilities(facs);
    }).finally(() => setLoading(false));
  }, []);

  const generateReport = async (reportType) => {
    setGenerating(reportType);
    setActiveReport(reportType);
    try {
      // Gather data for the report
      const [tasksRes, facilitiesRes, sourcesRes] = await Promise.all([
        taskAPI.getAll({ facilityId: selectedFacility }),
        facilityAPI.getAll(),
        api.get(`/sources${selectedFacility ? `?facilityId=${selectedFacility}` : ''}`)
      ]);

      const tasks = tasksRes.data.tasks || [];
      const allFacilities = facilitiesRes.data.facilities || [];
      const sources = sourcesRes.data.sources || [];

      // Build report data
      const data = {
        reportType,
        generatedAt: new Date().toISOString(),
        facility: selectedFacility ? allFacilities.find(f => f.id === parseInt(selectedFacility)) : null,
        tasks,
        facilities: allFacilities,
        sources,
        stats: {
          totalTasks: tasks.length,
          overdue: tasks.filter(t => t.status === 'overdue').length,
          completed: tasks.filter(t => t.status === 'completed').length,
          pending: tasks.filter(t => ['pending', 'in_progress'].includes(t.status)).length,
          totalFacilities: allFacilities.length,
          totalSources: sources.length,
          sourcesWithLimits: sources.filter(s => s.limits?.length > 0).length,
        }
      };
      setReportData(data);
    } catch (err) {
      toast.error('Failed to generate report');
    } finally {
      setGenerating(null);
    }
  };

  const downloadReport = async (reportType) => {
    if (!reportData) return;
    setGenerating(`download_${reportType}`);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/audit/report/${selectedFacility || (facilities[0]?.id || 1)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}-report-${Date.now()}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report downloaded!');
    } catch (err) {
      toast.error('Failed to download report');
    } finally {
      setGenerating(null);
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: '#6B7280' }}>Loading...</div>;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div className="page-title">Reports</div>
        <div className="page-subtitle">Pre-built analytics reports across your compliance data</div>
      </div>

      {/* Facility selector */}
      <div className="card" style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', fontFamily: 'var(--font-body)', whiteSpace: 'nowrap' }}>Report for:</div>
        <select value={selectedFacility} onChange={e => { setSelectedFacility(e.target.value); setReportData(null); setActiveReport(null); }}
          style={{ maxWidth: 300, padding: '8px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, fontFamily: 'var(--font-body)' }}>
          <option value="">All facilities</option>
          {facilities.map(f => <option key={f.id} value={f.id}>{f.facility_name || f.name}</option>)}
        </select>
      </div>

      {/* Report type cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 20 }}>
        {REPORT_TYPES.map(report => {
          const Icon = report.icon;
          const isActive = activeReport === report.id;
          const isGenerating = generating === report.id;
          return (
            <div key={report.id} className="card" style={{ padding: '11px 14px', border: isActive ? `2px solid ${report.color}` : '1px solid #E2E8F0', cursor: 'pointer' }}
              onClick={() => !isGenerating && generateReport(report.id)}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 16 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: report.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={18} color={report.color} />
                </div>
                <div>
                  <div style={{ fontFamily: 'var(--font-heading)', fontSize: 15, fontWeight: 700, color: '#0A1628', marginBottom: 4 }}>{report.title}</div>
                  <div style={{ fontSize: 14, color: '#6B7280', fontFamily: 'var(--font-body)', lineHeight: 1.4 }}>{report.desc}</div>
                </div>
              </div>
              <button
                onClick={e => { e.stopPropagation(); generateReport(report.id); }}
                disabled={isGenerating}
                style={{
                  width: '100%', padding: '8px', borderRadius: 8, fontSize: 14, fontWeight: 600,
                  fontFamily: 'var(--font-body)', cursor: isGenerating ? 'not-allowed' : 'pointer',
                  background: isActive ? report.bg : '#F8FAFC',
                  color: isActive ? report.color : '#374151',
                  border: `1px solid ${isActive ? report.color + '40' : '#E2E8F0'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                }}>
                {isGenerating
                  ? <><Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> Generating...</>
                  : isActive ? '↻ Refresh Report' : 'Generate Report'
                }
              </button>
            </div>
          );
        })}
      </div>

      {/* Report results */}
      {reportData && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700, color: '#0A1628' }}>
                {REPORT_TYPES.find(r => r.id === activeReport)?.title}
              </div>
              <div style={{ fontSize: 14, color: '#94a3b8', fontFamily: 'var(--font-body)', marginTop: 2 }}>
                Generated {formatDate(reportData.generatedAt)} · {reportData.facility ? reportData.facility.facility_name || reportData.facility.name : 'All facilities'}
              </div>
            </div>
            <button onClick={() => downloadReport(activeReport)} disabled={generating?.startsWith('download')} className="btn btn-ghost" style={{ fontSize: 13 }}>
              <Download size={14} /> {generating?.startsWith('download') ? 'Downloading...' : 'Download .docx'}
            </button>
          </div>

          {/* Stats grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 18 }}>
            {[
              { label: 'Total tasks', value: reportData.stats.totalTasks, color: '#0A1628' },
              { label: 'Overdue', value: reportData.stats.overdue, color: reportData.stats.overdue > 0 ? '#DC2626' : '#16A34A' },
              { label: 'Completed', value: reportData.stats.completed, color: '#16A34A' },
              { label: 'Facilities', value: reportData.stats.totalFacilities, color: '#0066FF' },
            ].map((s, i) => (
              <div key={i} style={{ background: '#F8FAFC', borderRadius: 10, padding: '11px 14px', textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: 28, fontWeight: 800, color: s.color, letterSpacing: '-1px' }}>{s.value}</div>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.4px', marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Task breakdown */}
          {activeReport === 'task_completion' && (
            <div>
              <div className="section-label" style={{ marginBottom: 12 }}>Task Breakdown</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {reportData.tasks.slice(0, 10).map(task => (
                  <div key={task.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#F8FAFC', borderRadius: 8 }}>
                    <div style={{ width: 3, height: 28, borderRadius: 2, background: task.status === 'completed' ? '#16A34A' : task.status === 'overdue' ? '#DC2626' : '#D97706', flexShrink: 0 }} />
                    <div style={{ flex: 1, fontSize: 14, fontWeight: 500, color: '#0A1628', fontFamily: 'var(--font-body)' }}>{task.title}</div>
                    <span className="badge" style={task.status === 'completed' ? { background: '#F0FDF4', color: '#16A34A' } : task.status === 'overdue' ? { background: '#FEF2F2', color: '#DC2626' } : { background: '#FFFBEB', color: '#D97706' }}>
                      {task.status}
                    </span>
                  </div>
                ))}
                {reportData.tasks.length > 10 && (
                  <div style={{ textAlign: 'center', fontSize: 14, color: '#94a3b8', fontFamily: 'var(--font-body)', padding: 8 }}>
                    + {reportData.tasks.length - 10} more tasks in downloaded report
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Emissions summary */}
          {activeReport === 'emissions_trend' && (
            <div>
              <div className="section-label" style={{ marginBottom: 12 }}>Emission Sources</div>
              {reportData.sources.length === 0 ? (
                <div style={{ fontSize: 14, color: '#6B7280', fontFamily: 'var(--font-body)', padding: 20, textAlign: 'center' }}>No emission sources found — add sources and log emissions to see trends</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {reportData.sources.map(source => (
                    <div key={source.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: '#F8FAFC', borderRadius: 8 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#0A1628', fontFamily: 'var(--font-body)' }}>
                          {source.source_id && <span style={{ color: '#94a3b8', marginRight: 8 }}>{source.source_id}</span>}
                          {source.name}
                        </div>
                        <div style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'var(--font-body)', marginTop: 2 }}>
                          {source.source_type} · {source.limits?.length || 0} limits tracked
                        </div>
                      </div>
                      <span className="badge" style={{ background: '#EFF6FF', color: '#0066FF' }}>{source.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Compliance status */}
          {(activeReport === 'compliance_status' || activeReport === 'facility_summary') && (
            <div>
              <div className="section-label" style={{ marginBottom: 12 }}>Facility Status</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {reportData.facilities.map(facility => {
                  const facilityTasks = reportData.tasks.filter(t => t.facility_id === facility.id);
                  const overdueCount = facilityTasks.filter(t => t.status === 'overdue').length;
                  return (
                    <div key={facility.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 12px', background: overdueCount > 0 ? '#FEF2F2' : '#F0FDF4', borderRadius: 8, border: `1px solid ${overdueCount > 0 ? '#FECACA' : '#BBF7D0'}` }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#0A1628', fontFamily: 'var(--font-body)' }}>{facility.facility_name || facility.name}</div>
                        <div style={{ fontSize: 12, color: '#6B7280', fontFamily: 'var(--font-body)', marginTop: 2 }}>
                          {facility.state && `${facility.state} · `}{facilityTasks.length} tasks
                        </div>
                      </div>
                      {overdueCount > 0
                        ? <span className="badge" style={{ background: '#FEF2F2', color: '#DC2626' }}><AlertTriangle size={11} style={{ display: 'inline', marginRight: 4 }} />{overdueCount} overdue</span>
                        : <span className="badge" style={{ background: '#F0FDF4', color: '#16A34A' }}><CheckCircle size={11} style={{ display: 'inline', marginRight: 4 }} />Current</span>
                      }
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <style>{`
        @media (max-width: 768px) {
          .mobile-stack { flex-direction: column !important; }
          .mobile-full { width: 100% !important; min-width: 0 !important; }
          .mobile-hide { display: none !important; }
          .mobile-grid-1 { grid-template-columns: 1fr !important; }
          .mobile-grid-2 { grid-template-columns: repeat(2, 1fr) !important; }
          .mobile-p { padding: 12px 14px !important; }
          .mobile-font { font-size: 13px !important; }
        }
      `}</style>
    </div>
  );
}

export default Reports;
